import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import interpolateContent from '../../../helpers/interpolate-content';
import useDebounce from '../../../hooks/use-debounce/use-debounce';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import { FavouriteBasketType } from '../../../models/favourite-basket-type';
import { FavouriteBasketsType } from '../../../models/favourite-baskets-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import PlacementEnum from '../../../models/placement-enum';
import { ProductBasketLineItemType } from '../../../models/product-basket-line-item-type';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import ProductTypeEnum from '../../../models/product-type-enum';
import favouritesAll from '../../../services/customer-api/endpoints/favourites/all';
import favouritesDelete from '../../../services/customer-api/endpoints/favourites/delete';
import favouritesGet from '../../../services/customer-api/endpoints/favourites/get';
import favouritesLineDelete from '../../../services/customer-api/endpoints/favourites/line/delete';
import favouritesLineUpdate from '../../../services/customer-api/endpoints/favourites/line/update';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import ButtonSecondary from '../../atoms/button-secondary/button-secondary';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import AlertModal from '../../molecules/alert-modal/alert-modal';
import BasketTable from '../../molecules/basket-table/basket-table';
import FavouriteBasketsAddForm from '../../molecules/favourite-baskets-add-form/favourite-baskets-add-form';
import FavouriteBasketsList from '../../molecules/favourite-baskets-list/favourite-baskets-list';
import NoDataBlock from '../../molecules/no-data-block/no-data-block';
import useAddMultipleToBasket from '../../providers/basket/hooks/use-add-multiple-to-basket';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import pricingRequest from '../../../services/customer-api/endpoints/pricing/request';
import { SpecialPricingType } from '../../../models/special-pricing-type';

const FavouriteBaskets: FC<FavouriteBasketsType> = (
  props: FavouriteBasketsType
) => {
  const {
    addToBasketButtonText,
    addProductErrorHeading,
    addProductErrorDescription,
    addProductErrorBody,
    insufficientClearanceStockError,
    clearQuoteAccept,
    clearQuoteCancel,
    clearQuoteDescription,
    clearQuoteHeading,
    clearQuoteHelp,
    deleteBasketButtonText,
    deleteFavouriteBasketDescription,
    deleteFavouriteBasketHeading,
    deleteFavouriteBasketNoButtonText,
    deleteFavouriteBasketYesButtonText,
    discountColumnLabel,
    emptyBasketDescription,
    emptyBasketHeading,
    itemsAddedToBasketDescription,
    itemsAddedToBasketHeading,
    newBasketDescription,
    newBasketHeading,
    noBasketsDescription,
    noBasketsHeading,
    priceColumnLabel,
    productDetailsColumnLabel,
    quantityColumnLabel,
    removeButtonLabel,
    removeLinkText,
    stockColumnLabel,
    subTotalColumnLabel,
    title,
    updateLinkText,
    updateQuantityLinkLabel,
    xProductsLabel,
  } = props;
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const [baskets, setBaskets] = useState<FavouriteBasketType[]>(null);
  const [basketItems, setBasketItems] = useState<ProductLineItemType[]>(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState<string>('');
  const debouncedFilterSearchTerm = useDebounce(filterSearchTerm);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedBasket, setSelectedBasket] = useState<FavouriteBasketType>();
  const [discountedPrices, setDiscountedPrices] =
    useState<SpecialPricingType[]>(null);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const addMultipleToBasket = useAddMultipleToBasket(
    {
      addProductErrorHeading,
      addProductErrorDescription,
      addProductErrorBody,
      insufficientClearanceStockError,
    },
    {
      clearQuoteAccept,
      clearQuoteCancel,
      clearQuoteDescription,
      clearQuoteHeading,
      clearQuoteHelp,
    }
  );

  const getBasketItems = (basket: FavouriteBasketType): void => {
    setSelectedBasket(basket);
    setIsLoading(true);

    favouritesGet(basket.id).then(
      (response: { lineItems: ProductLineItemType[] }) => {
        setBasketItems(response.lineItems.filter(({ product }) => !!product));
        setIsLoading(false);
        setLoading(false);
      }
    );
  };

  const getAll = (): void => {
    setIsLoading(true);
    favouritesAll({
      query: filterSearchTerm?.length ? filterSearchTerm : undefined,
    }).then((response: FavouriteBasketType[]) => {
      setBaskets(response);
      if (response?.length) {
        getBasketItems(
          response.sort((a: FavouriteBasketType, b: FavouriteBasketType) =>
            a.name.localeCompare(b.name)
          )[0]
        );
      } else {
        setSelectedBasket(null);
        setIsLoading(false);
        setBasketItems(null);
      }

      if (filterSearchTerm?.length >= 3) {
        pushToDataLayer(GtmEventNameEnum.FavouritesSearch);
      }
    });
  };

  const openCreateAlertModal = (basket: FavouriteBasketType): void => {
    overlayDispatch(
      open(
        <AlertModal
          heading={newBasketHeading}
          message={newBasketDescription.replace(
            '{0}',
            `<b>${basket?.name || 'Unknown'}</b>`
          )}
        />
      )
    );
  };

  const favouriteBasketItems = () => {
    if (!discountedPrices) {
      return basketItems;
    }
    return basketItems.map((item, index) => ({
      ...item,
      discount: discountedPrices[index]?.discount,
      buyPrice: discountedPrices[index]?.price,
      buyPriceFormatted: discountedPrices[index]?.priceFormatted,
      lineValueFormatted: discountedPrices[index]?.priceFormatted,
    }));
  };

  useEffect(() => {
    if (!basketItems) {
      return;
    }
    pricingRequest([...basketItems]).then((response: SpecialPricingType[]) =>
      setDiscountedPrices(response)
    );
  }, [basketItems]);

  const openBasketItemsAddedModal = (): void => {
    overlayDispatch(
      open(
        <AlertModal
          heading={itemsAddedToBasketHeading}
          message={itemsAddedToBasketDescription}
        />
      )
    );
  };

  const openDeleteAlertModal = (basket: FavouriteBasketType): void => {
    overlayDispatch(
      open(
        <AlertModal
          heading={deleteFavouriteBasketHeading}
          message={deleteFavouriteBasketDescription}
          cancelMessage={deleteFavouriteBasketNoButtonText}
          submitMessage={deleteFavouriteBasketYesButtonText}
          onSubmit={async () => {
            await favouritesDelete(basket.id);
            getAll();

            pushToDataLayer(GtmEventNameEnum.FavouritesDelete);
          }}
        />
      )
    );
  };

  const onLineItemUpdateQuantity = async (
    item: ProductLineItemType,
    quantity: number
  ): Promise<void> => {
    await favouritesLineUpdate(selectedBasket.id, item.orderLineId, quantity);
    getBasketItems(selectedBasket);

    pushToDataLayer(GtmEventNameEnum.ProductUpdate, {
      placement: PlacementEnum.FavouriteBaskets,
      productType: ProductTypeEnum.Website,
    });
  };

  const onLineItemDelete = async (item: ProductLineItemType): Promise<void> => {
    await favouritesLineDelete(selectedBasket.id, item.orderLineId);
    getBasketItems(selectedBasket);
  };

  const addAllToBasketMutation = useMutation(async () => {
    await addMultipleToBasket(
      basketItems.map(({ quantity, product: { variantSku } }) => ({
        variantSku,
        quantity,
      }))
    );

    openBasketItemsAddedModal();

    pushToDataLayer(GtmEventNameEnum.FavouritesAdd);

    pushToDataLayer(GtmEventNameEnum.AddToCart, {
      ecommerce: {
        add: {
          products: basketItems.map((item: ProductBasketLineItemType) => ({
            dimension3: item.product.variantSku,
            price: item.listPriceFormatted,
            quantity: item.quantity,
          })),
        },
      },
      placement: PlacementEnum.FavouriteBaskets,
      productType: ProductTypeEnum.Website,
    });
  });

  useEffect(() => {
    getAll();
  }, [debouncedFilterSearchTerm]);

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing">
      <h5 className="page-heading-five">{title}</h5>

      <hr className="border-grey-100 mb-6 mt-4" />

      <div className="lg:flex">
        <div className="w-full lg:w-3/12 lg:mr-4">
          <FavouriteBasketsAddForm
            {...props}
            onSuccess={(allBaskets: FavouriteBasketType[]) => {
              getAll();
              openCreateAlertModal(allBaskets[allBaskets.length - 1]);
              getBasketItems(allBaskets[allBaskets.length - 1]);

              pushToDataLayer(GtmEventNameEnum.FavouritesNew);
            }}
          />

          <hr className="border-grey-100 mb-8 mt-6" />

          {!baskets && <LoadingSpinner className="h-96" />}

          <AnimatePresence>
            {baskets && (
              <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                <FavouriteBasketsList
                  {...props}
                  baskets={baskets}
                  onChange={(value: FavouriteBasketType) => {
                    setLoading(true);
                    getBasketItems(value);
                  }}
                  onSearch={(value: string) => setFilterSearchTerm(value)}
                  selectedBasket={selectedBasket}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="w-full lg:w-9/12 lg:pl-8">
          {loading && <LoadingSpinner className="h-96" />}

          <AnimatePresence>
            {baskets && !baskets.length && !loading && (
              <motion.div
                animate={{ opacity: 1 }}
                className="mt-8 lg:mt-1"
                initial={{ opacity: 0 }}
              >
                <NoDataBlock
                  heading={noBasketsHeading}
                  message={noBasketsDescription}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {basketItems && !basketItems.length && !loading && (
              <motion.div
                animate={{ opacity: 1 }}
                className="mt-8 lg:mt-1"
                initial={{ opacity: 0 }}
              >
                <NoDataBlock
                  heading={emptyBasketHeading}
                  message={emptyBasketDescription}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {basketItems?.length && !loading && (
              <motion.div
                animate={{ opacity: 1 }}
                className="mt-8 lg:mt-1"
                initial={{ opacity: 0 }}
              >
                <div className="flex items-end mb-6">
                  <h4 className="page-heading-four -mb-1px">
                    {selectedBasket?.name}&nbsp;
                  </h4>

                  <span className="font-bold mb-2px text-grey-600 text-sm">
                    {interpolateContent(
                      xProductsLabel,
                      basketItems
                        .map((item: ProductLineItemType) => item.quantity)
                        .reduce((acc, cur) => acc + cur)
                    )}
                  </span>
                </div>

                <div
                  className={isLoading ? 'animate-pulse filter grayscale' : ''}
                >
                  <BasketTable
                    labels={{
                      discountColumnLabel,
                      priceColumnLabel,
                      productDetailsColumnLabel,
                      quantityColumnLabel,
                      removeButtonLabel,
                      removeLinkText,
                      stockColumnLabel,
                      subTotalColumnLabel,
                      updateLinkText,
                      updateQuantityLinkLabel,
                    }}
                    features={{
                      addButton: false,
                      checkoutLayout: false,
                      discountColumn: true,
                      favouritesLayout: true,
                      inStockColumn: false,
                      productWarnings: false,
                      quantityBreaksSave: false,
                      quantityReadonly: false,
                      recommendedAlternatives: false,
                      removeButton: true,
                      replaceButton: false,
                      subtotalColumn: true,
                      updateQuantityButton: true,
                    }}
                    lineItems={favouriteBasketItems()}
                    onLineUpdateQuantity={(lineItem, quantity) =>
                      onLineItemUpdateQuantity(
                        lineItem as ProductLineItemType,
                        quantity
                      )
                    }
                    onLineRemove={(lineItem) =>
                      onLineItemDelete(lineItem as ProductLineItemType)
                    }
                    {...props}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {basketItems && !loading && (
              <motion.div
                animate={{ opacity: 1 }}
                className="flex flex-wrap justify-end mt-6 md:space-x-4 space-y-4 md:space-y-0"
                initial={{ opacity: 0 }}
              >
                <ButtonSecondary
                  name={deleteBasketButtonText}
                  className="w-full md:w-auto"
                  onClick={() => openDeleteAlertModal(selectedBasket)}
                />
                {!!basketItems.length && (
                  <ButtonPrimary
                    className="w-full md:w-auto"
                    name={addToBasketButtonText}
                    disabled={addAllToBasketMutation.isLoading}
                    onClick={() => addAllToBasketMutation.mutate()}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default FavouriteBaskets;
